import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { generatePath, useNavigate } from "react-router-dom";

import { Button, Grid, Stack, Typography } from "@mui/material";
import { ArrowBack, AttachFile, CreateOutlined } from "@mui/icons-material";

import externalUserService from "../../../../../services/externalUserService";
import { AdminRoutesConfig, CommonRoutesConfig } from "../../../../RoutesConfig";
import CertificatesHistory from "../../../../shared/CertificatesHistory";
import SectionPreview from "../../../../../common/components/SectionPreview";
import { ExternalUserPreviewDto } from "../../../../../interfaces/admin/ExternalUser";
import { translate } from "../../../../../common/utils/formatters";
import { dateToStringShort } from "../../../../../common/utils/dateTime";
import { EnumNomenclature } from "../../../../../common/components/mui-extended/CheckboxSelectExtended";

export default function Preview() {
  const { id: paramsId } = useParams();
  const externalUserId: number = parseInt(paramsId as string);
  const navigate = useNavigate();
  const [externalUser, setExternalUser] = useState<ExternalUserPreviewDto>();
  const [permissionOptions, setPermissionOptions] = useState<Array<EnumNomenclature>>([]);

  const loadPermissionOptions = () => {
    (async () => {
      await externalUserService
        .getPermissionRegistersAsync()
        .then(({ data: options }) => setPermissionOptions(options));
    })();
  };

  useEffect(() => {
    loadPermissionOptions();

    if (isNaN(externalUserId)) {
      navigate(CommonRoutesConfig.NotFound.Path, { replace: true });
    }

    (async () => {
      await externalUserService.getExternalUserAsync(externalUserId).then(({ data: externalUser }) => {
        const model: ExternalUserPreviewDto = {
          ...externalUser,
        };

        setExternalUser(model);
      });
    })();
  }, []);

  const handleEdit = () => {
    navigate(generatePath(AdminRoutesConfig.ExternalUser.Edit.Path, { id: externalUserId.toString() }));
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between">
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Назад
        </Button>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<CreateOutlined />} onClick={() => handleEdit()}>
            Редактирай
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={3}>
        <SectionPreview title="Лични данни">
          <Stack>
            <Typography>Име</Typography>
            <Typography>
              <strong>{externalUser?.name}</strong>
            </Typography>
          </Stack>
        </SectionPreview>
        <SectionPreview title="Потребителски данни">
          <Stack spacing={3}>
            <Grid container rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <Typography>Потребителско име</Typography>
                <Typography>
                  <strong>{externalUser?.username}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>Парола</Typography>
                <Typography>
                  <strong>{externalUser?.password}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>SHA-1 на публичния ключ (на електронния подпис)</Typography>
                <Typography>
                  <strong>{externalUser?.thumbprint}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>Активен</Typography>
                <Typography>
                  <strong>{translate(!!externalUser?.isActive)}</strong>
                </Typography>
              </Grid>
            </Grid>
            {externalUser?.hasCertificateContent && (
              <Grid item xs={12} md={6}>
                <Button
                  startIcon={<AttachFile />}
                  variant="outlined"
                  href={`${process.env.REACT_APP_API_URL}/user/download/certificate/${externalUser?.thumbprint}`}
                >
                  Изтегли сертификат
                </Button>
              </Grid>
            )}
          </Stack>
        </SectionPreview>
        <SectionPreview title="Права">
          <Stack spacing={3}>
            <Grid container rowSpacing={2}>
              <Grid item xs={12} md={4}>
                <Typography>Активен от дата</Typography>
                <Typography>
                  <strong>{dateToStringShort(externalUser?.validFrom)}</strong>{" "}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography>Активен до дата</Typography>
                <Typography>
                  <strong>{dateToStringShort(externalUser?.validTo)}</strong>{" "}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography component="span" sx={{ mr: 1 }}>
                  Права:
                </Typography>
                {externalUser?.permissionsForRegister.map((item, index, array) => (
                  <Typography key={item} component="span">
                    <strong>
                      {permissionOptions.find((o) => o.value === item)?.name}
                      {array.length - 1 > index ? ", " : null}
                    </strong>
                  </Typography>
                ))}
              </Grid>
            </Grid>
          </Stack>
        </SectionPreview>
        <CertificatesHistory certificates={externalUser?.certificates} />
      </Stack>
    </Stack>
  );
}
